import { Header } from '../components/Header.js';
import { Button } from '../components/primitives/Button.js';
import { CONNECTOR_CATEGORIES } from '../data/connectors.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import './Docs.css';

export type DocsArticle = 'duplicates' | 'connectors';

export interface DocsProps {
  article: DocsArticle;
  onNavigate: (article: DocsArticle) => void;
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onToggleHelp: () => void;
}

interface NavGroup {
  label: string;
  items: Array<{ label: string; article: DocsArticle }>;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Getting started',
    items: [
      { label: 'Quickstart', article: 'duplicates' },
      { label: 'Connect a system', article: 'connectors' },
      { label: 'Data residency (EU / US)', article: 'duplicates' },
      { label: 'Roles & permissions', article: 'duplicates' },
    ],
  },
  {
    label: 'Connectors',
    items: [
      { label: 'CRM & go-to-market tools', article: 'connectors' },
      { label: 'Finance & billing tools', article: 'connectors' },
      { label: 'Support, docs & ops', article: 'connectors' },
      { label: 'Generic REST & CSV', article: 'connectors' },
    ],
  },
  {
    label: 'Core concepts',
    items: [
      { label: 'Sync & profiling', article: 'duplicates' },
      { label: 'Duplicate detection & review', article: 'duplicates' },
      { label: 'Survivorship rules', article: 'duplicates' },
      { label: 'Cleanup actions & safety', article: 'duplicates' },
      { label: 'Cross-system reconciliation', article: 'duplicates' },
    ],
  },
  {
    label: 'Developers',
    items: [
      { label: 'REST API', article: 'connectors' },
      { label: 'Webhooks', article: 'duplicates' },
      { label: 'MCP server', article: 'duplicates' },
      { label: 'Rate limits', article: 'duplicates' },
    ],
  },
];

const TIERS = [
  { score: '≥ 0.95', tier: 'auto_merge', tone: 'strong' as const, what: 'Queued for bulk approval. Locked until the workspace has reviewed 100 pairs at ≥98% sampled precision.' },
  { score: '0.70 – 0.95', tier: 'review', tone: 'warn' as const, what: 'Appears in the review queue with full field-level evidence. Median decision time under 30 seconds.' },
  { score: '< 0.70', tier: 'ignore', tone: 'muted' as const, what: 'Retained for audit, never proposed. Re-opens only if the evidence set changes.' },
];

const ON_THIS_PAGE_DUPLICATES = [
  { id: 'candidates', label: 'Candidate generation' },
  { id: 'tiers', label: 'Scoring tiers' },
  { id: 'review', label: 'The review queue' },
  { id: 'api', label: 'Reading clusters over the API' },
];

const ON_THIS_PAGE_CONNECTORS = [
  { id: 'native', label: 'Native connectors' },
  { id: 'directory', label: 'Full tool directory' },
  { id: 'bring-your-own', label: 'REST API & CSV/XLSX' },
  { id: 'register', label: 'Registering a custom connector' },
];

function DuplicatesArticle({ ccyCode, onToggleHelp }: { ccyCode: CurrencyCode; onToggleHelp: () => void }) {
  return (
    <>
      <div className="land-docs__breadcrumb">
        <span>Core concepts</span>
        <span>/</span>
        <span className="land-docs__breadcrumb-current">Duplicate detection &amp; review</span>
      </div>
      <h1>Duplicate detection &amp; review</h1>
      <p className="land-docs__intro">
        How Dedupr generates candidate pairs across any connected system, scores them into tiers,
        and hands every consequential decision to a human before anything is written.
      </p>
      <div className="land-docs__meta">
        <span className="land-docs__chip">updated 2026-07-24</span>
        <span className="land-docs__chip land-docs__chip--tint">feature F03</span>
        <span className="land-docs__chip">8 min read</span>
      </div>

      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          Detection never writes. Everything on this page happens against staged copies of your
          records — the first write only occurs after an action is approved in{' '}
          <a href="#docs-top">Cleanup actions &amp; safety</a>.
        </div>
      </div>

      <h2 id="candidates">1 · Candidate generation</h2>
      <p>
        Dedupr never compares every record to every other record — including across systems. It
        derives <strong>blocking keys</strong> (normalised email local-parts, phone digits,
        company-name-plus-domain composites, VAT / company numbers) and only compares records
        that share a key. A key value appearing more than 100 times — say{' '}
        <code>info@company.co.uk</code> — is demoted: it stops acting as a blocking key and is
        raised as a data-quality finding instead.
      </p>
      <div className="land-docs__code-block">
        <div className="land-docs__code-head">
          <span>blocking-keys.yaml</span>
          <button className="land-btn land-btn--plain">Copy</button>
        </div>
        <pre>
          <span className="land-docs__code-key">keys</span>:{'\n'}
          {'  - '}
          <span className="land-docs__code-green">email_local</span>
          {'       '}
          <span className="land-docs__code-comment"># strip dots, +suffixes, lowercase</span>
          {'\n  - '}
          <span className="land-docs__code-green">phone_digits</span>
          {'      '}
          <span className="land-docs__code-comment"># E.164, last 9 digits</span>
          {'\n  - '}
          <span className="land-docs__code-green">company_domain</span>
          {'    '}
          <span className="land-docs__code-comment"># metaphone(name) + web domain</span>
          {'\n  - '}
          <span className="land-docs__code-green">registration_no</span>
          {'   '}
          <span className="land-docs__code-comment"># UK company no. / VAT / EIN</span>
          {'\n'}
          <span className="land-docs__code-key">frequency_cap</span>: <span className="land-docs__code-amber">100</span>
          {'\n'}
          <span className="land-docs__code-key">cluster_size_cap</span>: <span className="land-docs__code-amber">25</span>
        </pre>
      </div>

      <h2 id="tiers">2 · Scoring tiers</h2>
      <p>
        Each candidate pair gets a score from weighted field comparators. The score routes the
        pair into one of three tiers. Thresholds are configurable per object type, within bounds.
      </p>
      <div className="land-docs__table">
        <div className="land-docs__table-head">
          <div>Score</div>
          <div>Tier</div>
          <div>What happens</div>
        </div>
        {TIERS.map((row) => (
          <div key={row.tier} className="land-docs__table-row">
            <div className={`land-docs__table-score land-docs__table-score--${row.tone}`}>{row.score}</div>
            <div className="land-docs__table-tier">{row.tier}</div>
            <div className="land-docs__table-what">{row.what}</div>
          </div>
        ))}
      </div>
      <div className="land-docs__warning">
        <strong>Bounded configuration.</strong> The auto-merge floor cannot be set below 0.90, on
        any plan. Fewer than 1% of candidate pairs are ever sent to an LLM adjudicator, and it
        receives only the match-relevant fields of the pair — never whole records.
      </div>

      <h2 id="review">3 · The review queue</h2>
      <p>
        Reviewers see the records side by side — whichever systems they came from — the
        comparators that fired with their weights, and the proposed surviving record. Human
        decisions are ground truth: a rejected pair is never re-proposed on the same evidence.
      </p>
      <div className="land-docs__image-placeholder">
        <div className="land-docs__image-tag">
          <span className="land-docs__image-dot" />
          Image placeholder · annotated screenshot · 16:10
        </div>
        <div className="land-docs__image-box">
          <p>
            AI PROMPT → "Documentation screenshot of a duplicate review queue: left list of
            duplicate groups with confidence badges, right detail pane with three record cards
            from different systems and a monospace evidence table, three numbered green
            annotation callouts (1,2,3) with thin leader lines, warm off-white UI #F6F4EF with
            white cards, 1px #E4DFD5 borders, deep green #0E5C43 accents, pill buttons, Instrument
            Sans, flat and crisp, 16:10."
          </p>
        </div>
      </div>

      <h2 id="api">4 · Reading clusters over the API</h2>
      <p>
        Clusters are read-only over both REST and the MCP server. Proposing an action returns{' '}
        <code>approval_required</code> until a human signs it off in the app. Monetary fields are
        returned in the workspace's billing currency ({ccyCode} for UK workspaces).
      </p>
      <div className="land-docs__code-block">
        <div className="land-docs__code-head">
          <span className="land-docs__code-verb">GET</span>
          /v1/clusters?tier=review&amp;limit=1
        </div>
        <pre>
          {'{\n  '}
          <span className="land-docs__code-key">"data"</span>: [{'\n    {\n      '}
          <span className="land-docs__code-key">"cluster_id"</span>: <span className="land-docs__code-green">"clu_9f13"</span>,{'\n      '}
          <span className="land-docs__code-key">"score"</span>: <span className="land-docs__code-amber">0.91</span>,{'\n      '}
          <span className="land-docs__code-key">"tier"</span>: <span className="land-docs__code-green">"review"</span>,{'\n      '}
          <span className="land-docs__code-key">"members"</span>: [<span className="land-docs__code-green">"crm:003xA1"</span>,{' '}
          <span className="land-docs__code-green">"ledger:CUST-4410"</span>, <span className="land-docs__code-green">"csv:events24#812"</span>],{'\n      '}
          <span className="land-docs__code-key">"evidence"</span>: [{'\n        { '}
          <span className="land-docs__code-key">"comparator"</span>: <span className="land-docs__code-green">"phone.exact"</span>,{' '}
          <span className="land-docs__code-key">"weight"</span>: <span className="land-docs__code-amber">0.41</span> {'},\n        { '}
          <span className="land-docs__code-key">"comparator"</span>: <span className="land-docs__code-green">"email.variant"</span>,{' '}
          <span className="land-docs__code-key">"weight"</span>: <span className="land-docs__code-amber">0.33</span> {'}\n      ],\n      '}
          <span className="land-docs__code-key">"recoverable_spend"</span>: {'{ '}
          <span className="land-docs__code-key">"currency"</span>: <span className="land-docs__code-green">"{ccyCode}"</span>,{' '}
          <span className="land-docs__code-key">"amount_year"</span>: <span className="land-docs__code-amber">5280</span> {'}\n    }\n  ],\n  '}
          <span className="land-docs__code-key">"next_cursor"</span>: <span className="land-docs__code-green">"eyJvIjoxMDB9"</span>
          {'\n}'}
        </pre>
      </div>

      <div className="land-docs__ask">
        <div>
          <div className="land-docs__ask-title">Still not sure how a score was reached?</div>
          <div className="land-docs__ask-sub">Ask the docs agent — it cites the page it answered from.</div>
        </div>
        <Button variant="primary" size="md" onClick={onToggleHelp}>
          ✻ Ask Dedupr
        </Button>
      </div>
    </>
  );
}

function ConnectorsArticle() {
  return (
    <>
      <div className="land-docs__breadcrumb">
        <span>Connectors</span>
        <span>/</span>
        <span className="land-docs__breadcrumb-current">CRM &amp; go-to-market tools</span>
      </div>
      <h1>Connecting your stack</h1>
      <p className="land-docs__intro">
        The full list of systems Dedupr can read from — which ones get a native, first-party
        connector today, which ones our generic connector reaches with a real OAuth
        "click-authorize" flow, and how everything else connects via a pasted API key or a
        CSV/XLSX upload.
      </p>
      <div className="land-docs__meta">
        <span className="land-docs__chip">updated 2026-07-28</span>
        <span className="land-docs__chip land-docs__chip--tint">feature F01</span>
        <span className="land-docs__chip">5 min read</span>
      </div>

      <h2 id="native">1 · Native connectors</h2>
      <p>
        Three systems get a dedicated OAuth app with guided setup and read-only scopes at connect
        time: <strong>Salesforce</strong>, <strong>HubSpot</strong>, and{' '}
        <strong>SharePoint / OneDrive</strong> (via Microsoft Graph). Write scopes are requested
        later, per action type, only the first time an action of that type is approved — never at
        connect.
      </p>

      <h2 id="directory">2 · Full tool directory</h2>
      <p>
        Everything below is a real system teams run go-to-market on. Marked tools connect through
        our generic connector via that vendor's own OAuth authorization flow — a customer clicks
        "authorize" once, no pasted key — verified against each vendor's own developer
        documentation, and deliberately excludes any tool whose OAuth only works via a
        vendor-side partner/approval program or a consent-screen-free machine credential. Everything
        else still connects today, just via an API key or CSV — see §3.
      </p>
      <div className="land-docs__connector-groups">
        {CONNECTOR_CATEGORIES.map((category) => (
          <div key={category.id}>
            <div className="land-docs__connector-group-label">{category.label}</div>
            <div className="land-docs__connector-group-tools">
              {category.tools.map((tool) => (
                <span
                  key={tool.name}
                  className={`land-tag${tool.native ? ' land-connectors__tool--native' : tool.oauth ? ' land-connectors__tool--oauth' : ''}`}
                >
                  {tool.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h2 id="bring-your-own">3 · REST API &amp; CSV/XLSX</h2>
      <p>
        Any authenticated JSON endpoint can be mapped as a source: point the connector at a base
        URL, provide credentials (API key or OAuth client), and map the response fields once. A
        CSV or XLSX export works the same way with no API at all — upload it, map the columns, and
        it becomes a pseudo-source you can delete whenever you like. Both paths run through the
        identical profiler, match engine and safety rails as a native connector.
      </p>

      <h2 id="register">4 · Registering a custom connector</h2>
      <div className="land-docs__code-block">
        <div className="land-docs__code-head">
          <span className="land-docs__code-verb">POST</span>
          /v1/sources
        </div>
        <pre>
          {'{\n  '}
          <span className="land-docs__code-key">"type"</span>: <span className="land-docs__code-green">"rest"</span>,{'\n  '}
          <span className="land-docs__code-key">"name"</span>: <span className="land-docs__code-green">"Lusha"</span>,{'\n  '}
          <span className="land-docs__code-key">"base_url"</span>: <span className="land-docs__code-green">"https://api.example.com/v2"</span>,{'\n  '}
          <span className="land-docs__code-key">"auth"</span>: {'{ '}
          <span className="land-docs__code-key">"type"</span>: <span className="land-docs__code-green">"api_key"</span>,{' '}
          <span className="land-docs__code-key">"header"</span>: <span className="land-docs__code-green">"X-Api-Key"</span> {'},\n  '}
          <span className="land-docs__code-key">"field_map"</span>: {'{ '}
          <span className="land-docs__code-key">"email"</span>: <span className="land-docs__code-green">"$.contact.email"</span>,{' '}
          <span className="land-docs__code-key">"company"</span>: <span className="land-docs__code-green">"$.contact.company_name"</span> {'}\n}'}
        </pre>
      </div>
      <div className="land-docs__warning">
        <strong>Read-only to start.</strong> A custom REST source is registered with GET-only
        credentials by default, exactly like a native connector — write access is a separate,
        later grant.
      </div>
    </>
  );
}

export function Docs({
  article,
  onNavigate,
  ccyCode,
  regionLabel,
  onCycleCurrency,
  onGoHome,
  onToggleHelp,
}: DocsProps) {
  const breadcrumbLabel = article === 'connectors' ? 'CRM & go-to-market tools' : 'Duplicate detection & review';
  const onThisPage = article === 'connectors' ? ON_THIS_PAGE_CONNECTORS : ON_THIS_PAGE_DUPLICATES;

  return (
    <section data-screen-label="Documentation" className="land-docs">
      <Header
        view="docs"
        ccyCode={ccyCode}
        regionLabel={regionLabel}
        onCycleCurrency={onCycleCurrency}
        onGoHome={onGoHome}
        onGoDocs={() => {}}
        onOpenAudit={() => {}}
        onOpenBooking={() => {}}
        onToggleHelp={onToggleHelp}
      />

      <div className="land-docs__layout">
        <aside className="land-docs__sidebar">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="land-docs__nav-group">
              <div className="land-docs__nav-label">{group.label}</div>
              <div className="land-docs__nav-links">
                {group.items.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className={item.label === breadcrumbLabel ? 'is-active' : ''}
                    onClick={() => onNavigate(item.article)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <article id="docs-top" className="land-docs__article">
          {article === 'connectors' ? (
            <ConnectorsArticle />
          ) : (
            <DuplicatesArticle ccyCode={ccyCode} onToggleHelp={onToggleHelp} />
          )}

          <div className="land-docs__pager">
            <button
              type="button"
              className="land-docs__pager-link"
              onClick={() => onNavigate(article === 'connectors' ? 'duplicates' : 'connectors')}
            >
              <div className="land-docs__pager-label">← Previous</div>
              <div className="land-docs__pager-title">
                {article === 'connectors' ? 'Duplicate detection & review' : 'Sync & profiling'}
              </div>
            </button>
            <button
              type="button"
              className="land-docs__pager-link land-docs__pager-link--next"
              onClick={() => onNavigate(article === 'connectors' ? 'duplicates' : 'connectors')}
            >
              <div className="land-docs__pager-label">Next →</div>
              <div className="land-docs__pager-title">
                {article === 'connectors' ? 'Survivorship rules' : 'Connecting your stack'}
              </div>
            </button>
          </div>
        </article>

        <aside className="land-docs__toc">
          <div className="land-docs__toc-label">On this page</div>
          <div className="land-docs__toc-links">
            {onThisPage.map((item, index) => (
              <a key={item.id} href={`#${item.id}`} className={index === 1 ? 'is-active' : ''}>
                {item.label}
              </a>
            ))}
          </div>
          <div className="land-docs__feedback">
            <div className="land-docs__feedback-title">Was this useful?</div>
            <div className="land-docs__feedback-buttons">
              <button className="land-btn land-btn--secondary land-btn--xs">Yes</button>
              <button className="land-btn land-btn--secondary land-btn--xs">No</button>
            </div>
            <a href="#docs-top" className="land-docs__edit">
              Edit this page ↗
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}
